import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MessageSquare, Clock, Inbox } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import PriorityPill from "../components/PriorityPill";
import SLATimer from "../components/SLATimer";
import { api } from "../api/client";

const STATUS_TONE = {
    open: "bg-emerald-100 text-emerald-700",
    awaiting_agent: "bg-blue-100 text-blue-700",
    awaiting_customer: "bg-amber-100 text-amber-800",
    resolved: "bg-zinc-100 text-zinc-700",
    closed: "bg-zinc-200 text-zinc-600",
};

const SupportContent = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let m = true;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/support/tickets");
                if (m) setTickets(data || []);
            } finally {
                if (m) setLoading(false);
            }
        };
        load();
        return () => { m = false; };
    }, []);

    if (loading) {
        return <div className="space-y-3"><div className="h-24 skeleton rounded-2xl" /><div className="h-24 skeleton rounded-2xl" /></div>;
    }

    if (tickets.length === 0) {
        return (
            <EmptyState
                icon={<Inbox className="h-10 w-10 text-brand-600" />}
                title="No support tickets yet"
                description="Need help with an order, prescription, or anything else? We're here."
                action={<Link to="/support/new" className="btn-brand"><Plus className="h-4 w-4" /> Open ticket</Link>}
            />
        );
    }

    return (
        <div className="space-y-3" data-testid="support-tickets-list">
            {tickets.map((t) => (
                <Link
                    to={`/support/${t.id}`}
                    key={t.id}
                    className="block rounded-2xl border border-zinc-100 bg-white p-5 transition-all hover:border-brand-200 hover:shadow-sm"
                    data-testid={`support-ticket-${t.id}`}
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-mono text-zinc-400">{t.ticket_no}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_TONE[t.status] || "bg-zinc-100 text-zinc-700"}`}>
                                    {t.status.replace("_", " ")}
                                </span>
                                <PriorityPill priority={t.priority} />
                            </div>
                            <h3 className="mt-1.5 text-base font-semibold text-zinc-900 line-clamp-1">{t.subject}</h3>
                            {t.last_message?.preview && (
                                <p className="mt-1 text-sm text-zinc-500 line-clamp-1">
                                    <span className="font-medium text-zinc-700">
                                        {t.last_message.sender_type === "agent" ? "PILLS:" : "You:"}
                                    </span>{" "}
                                    {t.last_message.preview}
                                </p>
                            )}
                        </div>
                        <div className="text-right">
                            <SLATimer slaDueAt={t.sla_due_at} slaBreached={t.sla_breached} status={t.status} />
                            <p className="mt-1 text-[10px] text-zinc-400">{new Date(t.updated_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
};

const Support = () => (
    <Layout>
        <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <p className="label-uppercase">Support</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">My tickets</h1>
                    <p className="mt-1 text-sm text-zinc-500">Track conversations with our care team. We aim to respond within SLA.</p>
                </div>
                <Link to="/support/new" className="btn-brand !py-2 !px-4 !text-sm" data-testid="open-ticket-btn">
                    <Plus className="h-4 w-4" /> New ticket
                </Link>
            </header>
            <AuthGuard>
                <SupportContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Support;
